import { Capacitor } from '@capacitor/core';
import * as ReactSentry from '@sentry/react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useRxDB } from 'rxdb-hooks';
import { filter, firstValueFrom } from 'rxjs';

import { refreshTokenAction } from '../actions/user-actions';
import { USER_META_ID } from '../db';
import { decodeJWT, isTokenValid } from '../utils/token-utils';

import { IUser } from './useUser';

// Global variable to track ongoing token refresh to ensure only one refresh at a time
let ongoingRefreshPromise: Promise<string> | null = null;

/**
 * Returns a function that can be used to refresh the access token. This
 * function will return a promise that will resolve with the value of the new
 * token after the token has been refreshed.
 *
 * If the token can't be refreshed for some reason, the promise will reject.
 * This can happen, for example, if the refresh token is expired/invalid, if the
 * user is not logged in when the function is called, or if the API request
 * fails.
 */
export function useTokenRefresher(): () => Promise<string> {
  const db = useRxDB();
  const dispatch = useDispatch();

  return useCallback(async () => {
    // If there's already a refresh in progress, return the existing promise
    if (ongoingRefreshPromise) {
      ReactSentry.addBreadcrumb({
        category: 'auth',
        message:
          'token refresh already in progress, waiting for existing refresh',
        data: {
          platform: Capacitor.getPlatform(),
        },
        level: 'info',
      });
      return ongoingRefreshPromise;
    }

    // Start a new refresh and track the promise
    ongoingRefreshPromise = (async () => {
      try {
        ReactSentry.addBreadcrumb({
          category: 'auth',
          message: 'starting new token refresh',
          data: {
            platform: Capacitor.getPlatform(),
          },
          level: 'info',
        });

        const doc = await db.getLocal<{ user: IUser }>(USER_META_ID);
        const user = doc._data.data.user;
        const currentToken = user.token;

        if (currentToken) {
          const decodedToken = decodeJWT(currentToken);
          if (isTokenValid(decodedToken.payload).valid) {
            // currentToken is valid - API will return 400 if we refresh valid token
            return currentToken;
          }
        }

        dispatch(refreshTokenAction());

        const nextDoc = await firstValueFrom(
          db.getLocal$<{ user: IUser }>(USER_META_ID).pipe(
            filter((nextDoc) => {
              // we wait for the update with the new token and skip all other updates, that may occur in the meantime
              return nextDoc._data.data.user?.token !== currentToken;
            })
          )
        );

        return nextDoc._data.data.user?.token;
      } finally {
        // Clear the ongoing promise when done (success or error)
        ongoingRefreshPromise = null;
      }
    })();

    return ongoingRefreshPromise;
  }, [db, dispatch]);
}
